import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../api';

const StockistPurchases = () => {
  const [allPurchases, setAllPurchases] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [stockists, setStockists] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bills');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('ALL');
  const [selectedPaymentType, setSelectedPaymentType] = useState('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Detail Modal
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showBillPhotoInStockist, setShowBillPhotoInStockist] = useState(false);

  // Add Stockist Modal & Stock Details Modal
  const [showAddStockistModal, setShowAddStockistModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockModalSearch, setStockModalSearch] = useState('');
  const [newStockist, setNewStockist] = useState({
    name: '',
    address: '',
    phone: '',
    gst: '',
    dlNo: '',
    email: '',
    notes: ''
  });
  const [isSavingStockist, setIsSavingStockist] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Return to Party Modal
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnSupplierName, setReturnSupplierName] = useState('');
  const [returnItems, setReturnItems] = useState([]);
  const [returnReason, setReturnReason] = useState('Expired Medicine Stock Return to Party');
  const [returnFilterTab, setReturnFilterTab] = useState('ALL'); // 'EXPIRED' | 'NEAR_EXPIRY' | 'ALL'
  const [isSubmittingReturn, setIsSubmittingReturn] = useState(false);

  // Helper to parse expiry string (MM/YY, MM/YYYY, MM-YY, YYYY-MM-DD)
  const checkExpiryStatus = (expStr) => {
    if (!expStr || expStr === '-' || expStr.trim() === '') {
      return { isExpired: false, isNearExpiry: false, status: 'NORMAL', label: 'Valid', badgeClass: 'bg-slate-100 text-slate-600' };
    }
    const clean = expStr.trim();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let expDate = null;
    const parts = clean.includes('/') ? clean.split('/') : clean.split('-');
    if (parts.length === 2) {
      const month = parseInt(parts[0], 10) - 1;
      let year = parseInt(parts[1], 10);
      if (year < 100) year += 2000;
      expDate = new Date(year, month + 1, 0, 23, 59, 59);
    } else if (parts.length === 3) {
      if (parts[0].length === 4) {
        expDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 23, 59, 59);
      } else {
        let year = parseInt(parts[2], 10);
        if (year < 100) year += 2000;
        expDate = new Date(year, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10), 23, 59, 59);
      }
    }

    if (!expDate || isNaN(expDate.getTime())) {
      return { isExpired: false, isNearExpiry: false, status: 'NORMAL', label: 'Valid', badgeClass: 'bg-slate-100 text-slate-600' };
    }

    if (expDate < today) {
      return { isExpired: true, isNearExpiry: false, status: 'EXPIRED', label: 'Expired ⚠️', badgeClass: 'bg-rose-100 text-rose-700 border border-rose-300 font-bold' };
    }

    const threeMonths = new Date(today);
    threeMonths.setMonth(today.getMonth() + 3);
    if (expDate <= threeMonths) {
      return { isExpired: false, isNearExpiry: true, status: 'NEAR_EXPIRY', label: 'Near Expiry ⏳', badgeClass: 'bg-amber-100 text-amber-800 border border-amber-300 font-bold' };
    }

    return { isExpired: false, isNearExpiry: false, status: 'VALID', label: 'Active', badgeClass: 'bg-emerald-50 text-emerald-700 border border-emerald-200' };
  };

  // Delete States for Bills, Stockists, and Credit Notes
  const [deleteConfirmPurchase, setDeleteConfirmPurchase] = useState(null);
  const [isDeletingPurchase, setIsDeletingPurchase] = useState(false);

  const [deleteConfirmStockist, setDeleteConfirmStockist] = useState(null);
  const [isDeletingStockist, setIsDeletingStockist] = useState(false);

  const [deleteConfirmCreditNote, setDeleteConfirmCreditNote] = useState(null);
  const [isDeletingCreditNote, setIsDeletingCreditNote] = useState(false);

  const navigate = useNavigate();

  // Handle F3 key press for editing purchase bill
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F3' || e.code === 'F3' || e.which === 114) {
        if (selectedPurchase) {
          e.preventDefault();
          const billToEdit = selectedPurchase;
          setSelectedPurchase(null);
          navigate('/dashboard/purchases', { state: { editBillId: billToEdit.id, editBill: billToEdit } });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPurchase, navigate]);

  const fetchData = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      else setIsRefreshing(true);

      const [purRes, salesRes, stockistRes, cnRes] = await Promise.all([
        fetch(apiUrl('/api/purchases')),
        fetch(apiUrl('/api/sales')).catch(() => ({ ok: false })),
        fetch(apiUrl('/api/stockists')),
        fetch(apiUrl('/api/credit-notes')).catch(() => ({ ok: false }))
      ]);
      if (purRes.ok) {
        const purData = await purRes.json();
        setAllPurchases(purData);
      }
      if (salesRes && salesRes.ok) {
        const salesData = await salesRes.json();
        setAllSales(salesData);
      }
      if (stockistRes.ok) {
        const stockistData = await stockistRes.json();
        setStockists(stockistData);
      }
      if (cnRes && cnRes.ok) {
        const cnData = await cnRes.json();
        setCreditNotes(cnData);
      }
    } catch (error) {
      console.error('Failed to fetch stockist purchases or stockists', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh data every 7 seconds in background for real-time sync across PCs
    const interval = setInterval(() => {
      fetchData(true);
    }, 7000);

    // Auto-refresh when window regains focus
    const handleFocus = () => {
      fetchData(true);
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Full Screen State & Toggle
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const handleSaveStockist = async (e) => {
    e.preventDefault();
    if (isSavingStockist) return;
    if (!newStockist.name.trim()) {
      alert('Stockist name is required');
      return;
    }

    const checkName = newStockist.name.trim().toUpperCase();
    const isDuplicate = stockists.some(s => s.name && s.name.trim().toUpperCase() === checkName);
    if (isDuplicate) {
      alert(`⚠️ Stockist "${newStockist.name.trim()}" already exists! Only one entry is allowed.`);
      return;
    }

    try {
      setIsSavingStockist(true);
      const res = await fetch(apiUrl('/api/stockists'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newStockist)
      });
      if (res.ok) {
        const saved = await res.json();
        setStockists(prev => [saved, ...prev]);
        setShowAddStockistModal(false);
        setNewStockist({ name: '', address: '', phone: '', gst: '', dlNo: '', email: '', notes: '' });
        fetchData(true);
        alert('Stockist saved successfully!');
      } else {
        const errJson = await res.json().catch(() => ({}));
        alert(errJson.error || 'Failed to save stockist');
      }
    } catch (err) {
      console.error('Error saving stockist:', err);
      alert('Error saving stockist: ' + err.message);
    } finally {
      setIsSavingStockist(false);
    }
  };

  const handleDeletePurchase = (purchase) => {
    setDeleteConfirmPurchase(purchase);
  };

  const confirmDeletePurchase = async () => {
    if (!deleteConfirmPurchase) return;
    const bill = deleteConfirmPurchase;
    try {
      setIsDeletingPurchase(true);
      let success = false;
      try {
        const res = await fetch(apiUrl(`/api/purchases/${bill.id}`), { method: 'DELETE' });
        if (res.ok) {
          success = true;
        }
      } catch (e) {
        console.warn('Delete attempt failed:', e);
      }

      if (!success) {
        throw new Error('Failed to delete purchase on backend server');
      }

      setAllPurchases(prev => prev.filter(p => p.id !== bill.id));
      if (selectedPurchase?.id === bill.id) {
        setSelectedPurchase(null);
      }
      setDeleteConfirmPurchase(null);
      fetchData(true);
    } catch (err) {
      console.error('Error deleting purchase:', err);
      alert('Failed to delete purchase: ' + err.message);
    } finally {
      setIsDeletingPurchase(false);
    }
  };

  const handleDeleteStockist = (stk) => {
    setDeleteConfirmStockist(stk);
  };

  const confirmDeleteStockist = async () => {
    if (!deleteConfirmStockist) return;
    const stk = deleteConfirmStockist;
    try {
      setIsDeletingStockist(true);
      if (stk.id) {
        await fetch(apiUrl(`/api/stockists/${stk.id}`), { method: 'DELETE' });
      }
      setStockists(prev => prev.filter(s => s.id !== stk.id && s.name?.toUpperCase() !== stk.name?.toUpperCase()));
      setDeleteConfirmStockist(null);
      fetchData(true);
    } catch (err) {
      console.error('Error deleting stockist:', err);
      alert('Failed to delete stockist: ' + err.message);
    } finally {
      setIsDeletingStockist(false);
    }
  };

  const handleDeleteCreditNote = (cn) => {
    setDeleteConfirmCreditNote(cn);
  };

  const confirmDeleteCreditNote = async () => {
    if (!deleteConfirmCreditNote) return;
    const cn = deleteConfirmCreditNote;
    try {
      setIsDeletingCreditNote(true);
      if (cn.id) {
        await fetch(apiUrl(`/api/credit-notes/${cn.id}`), { method: 'DELETE' });
      }
      setCreditNotes(prev => prev.filter(c => c.id !== cn.id));
      setDeleteConfirmCreditNote(null);
      fetchData(true);
    } catch (err) {
      console.error('Error deleting credit note:', err);
      alert('Failed to delete credit note: ' + err.message);
    } finally {
      setIsDeletingCreditNote(false);
    }
  };

  // Automatically compute & unify stockists list in real time from Firestore + all purchases
  const unifiedStockists = React.useMemo(() => {
    const map = new Map();

    // 1. Add registered stockists from Firestore
    (stockists || []).forEach(s => {
      if (!s || !s.name) return;
      const key = s.name.trim().toUpperCase();
      map.set(key, {
        id: s.id,
        name: s.name.trim(),
        phone: s.phone || s.mobile || '',
        gst: s.gst || s.gstNo || '',
        address: s.address || '',
        dlNo: s.dlNo || '',
        email: s.email || '',
        totalBills: s.totalBills || 0,
        totalPurchases: parseFloat(s.totalPurchases || 0),
        lastPurchaseDate: s.lastPurchaseDate || '',
      });
    });

    // 2. Automatically discover & aggregate directly from allPurchases
    (allPurchases || []).forEach(p => {
      const name = (p.supplierName || '').trim();
      if (!name) return;
      const key = name.toUpperCase();
      const existing = map.get(key) || {
        id: p.supplierId || key,
        name: name,
        phone: p.supplierPhone || p.phone || '',
        gst: p.supplierGst || p.gst || '',
        address: p.supplierAddress || p.address || '',
        dlNo: '',
        email: '',
        totalBills: 0,
        totalPurchases: 0,
        lastPurchaseDate: ''
      };

      if (!existing.phone && p.supplierPhone) existing.phone = p.supplierPhone;
      if (!existing.gst && p.supplierGst) existing.gst = p.supplierGst;
      if (!existing.address && p.supplierAddress) existing.address = p.supplierAddress;

      map.set(key, existing);
    });

    // 3. Compute accurate live metrics for each stockist
    const list = Array.from(map.values()).map(stk => {
      const key = (stk.name || '').trim().toUpperCase();
      const partyBills = (allPurchases || []).filter(p => (p.supplierName || '').trim().toUpperCase() === key);
      
      if (partyBills.length > 0) {
        const totalBills = partyBills.length;
        const totalPurchases = partyBills.reduce((acc, p) => acc + (parseFloat(p.netAmount || p.totalAmount || p.grossAmount || 0) || 0), 0);
        
        // Find latest bill date
        const sortedBills = [...partyBills].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        const lastPurchaseDate = sortedBills[0]?.date || stk.lastPurchaseDate || '';
        const lastInvoiceNo = sortedBills[0]?.invoiceNo || stk.lastInvoiceNo || '';

        return {
          ...stk,
          totalBills,
          totalPurchases,
          lastPurchaseDate,
          lastInvoiceNo
        };
      }
      return stk;
    });

    return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [stockists, allPurchases]);

  // Unique list of suppliers
  const suppliersList = Array.from(
    new Set(allPurchases.map(p => p.supplierName).filter(Boolean))
  ).sort();

  // Filtered Purchases
  const filteredPurchases = allPurchases.filter(p => {
    // Supplier filter
    if (selectedSupplier !== 'ALL' && p.supplierName !== selectedSupplier) return false;

    // Payment Type filter
    if (selectedPaymentType !== 'ALL' && (p.paymentType || 'Credit') !== selectedPaymentType) return false;

    // Date range filter
    if (fromDate && p.date && p.date < fromDate) return false;
    if (toDate && p.date && p.date > toDate) return false;

    // Text search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchSupplier = p.supplierName && p.supplierName.toLowerCase().includes(q);
      const matchInvoice = p.invoiceNo && p.invoiceNo.toLowerCase().includes(q);
      const matchBuyer = (p.buyerName && p.buyerName.toLowerCase().includes(q)) || (p.pharmacyName && p.pharmacyName.toLowerCase().includes(q));
      const matchItem = p.items && p.items.some(it => 
        (it.productName && it.productName.toLowerCase().includes(q)) ||
        (it.mfg && it.mfg.toLowerCase().includes(q)) ||
        (it.batch && it.batch.toLowerCase().includes(q))
      );
      if (!matchSupplier && !matchInvoice && !matchBuyer && !matchItem) return false;
    }

    return true;
  }).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  // Filtered Stockist Directory using unified list
  const filteredStockists = unifiedStockists.filter(s => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (s.name && s.name.toLowerCase().includes(q)) ||
      (s.phone && s.phone.includes(q)) ||
      (s.gst && s.gst.toLowerCase().includes(q)) ||
      (s.address && s.address.toLowerCase().includes(q))
    );
  });

  // Calculated KPI Stats
  const totalAmount = filteredPurchases.reduce((acc, p) => acc + (parseFloat(p.netAmount || p.totalAmount || p.grossAmount || 0) || 0), 0);
  const creditAmount = filteredPurchases
    .filter(p => (p.paymentType || 'Credit') === 'Credit')
    .reduce((acc, p) => acc + (parseFloat(p.netAmount || p.totalAmount || p.grossAmount || 0) || 0), 0);
  const cashAmount = filteredPurchases
    .filter(p => p.paymentType === 'Cash')
    .reduce((acc, p) => acc + (parseFloat(p.netAmount || p.totalAmount || p.grossAmount || 0) || 0), 0);

  // Calculate live available stock in medical store with detailed product list (accounting for sales & credit note returns)
  const availableStockMetrics = React.useMemo(() => {
    let totalPurchasedQty = 0;
    let totalSoldQty = 0;
    let totalReturnedToSupplierQty = 0;

    (allPurchases || []).forEach(p => {
      (p.items || []).forEach(it => {
        const billed = parseFloat(it.qty) || 0;
        const free = parseFloat(it.freeQty || it.sch || it.free || it.scheme || 0) || 0;
        totalPurchasedQty += (billed + free);
      });
    });

    (allSales || []).forEach(s => {
      (s.items || []).forEach(it => {
        totalSoldQty += (parseFloat(it.qty) || 0);
      });
    });

    (creditNotes || []).forEach(cn => {
      (cn.items || []).forEach(it => {
        totalReturnedToSupplierQty += (parseFloat(it.qty) || 0);
      });
    });

    const totalAvailableQty = Math.max(0, totalPurchasedQty - totalSoldQty - totalReturnedToSupplierQty);

    const getPacking = (item) => {
      if (!item) return '';
      return String(item.packing || item.package || item.pack || item.packSize || item.pkg || item.strip || '').trim();
    };

    const productStockMap = new Map();
    (allPurchases || []).forEach(p => {
      (p.items || []).forEach(it => {
        if (!it.productName) return;
        const nameClean = it.productName.trim();
        const packClean = getPacking(it);
        const nameKey = nameClean.toUpperCase();
        const packKey = packClean.toUpperCase();
        // Separate entries by both product name and package / packing
        const key = `${nameKey}||${packKey}`;

        if (!productStockMap.has(key)) {
          productStockMap.set(key, {
            name: nameClean,
            packing: packClean || '-',
            company: it.company || it.mfg || '-',
            batch: it.batch || '-',
            exp: it.exp || '-',
            rate: parseFloat(it.rate || it.mrp || 0) || 0,
            mrp: parseFloat(it.mrp || 0) || 0,
            purchasedQty: 0,
            soldQty: 0,
            returnedQty: 0,
            availableQty: 0,
            supplier: p.supplierName || 'Unknown'
          });
        }
        const prod = productStockMap.get(key);
        const billed = parseFloat(it.qty) || 0;
        const free = parseFloat(it.freeQty || it.sch || it.free || it.scheme || 0) || 0;
        prod.purchasedQty += (billed + free);
        if (packClean && (!prod.packing || prod.packing === '-')) {
          prod.packing = packClean;
        }
        if (it.batch && prod.batch === '-') {
          prod.batch = it.batch;
        } else if (it.batch && it.batch !== '-' && prod.batch && !prod.batch.split(', ').includes(it.batch)) {
          prod.batch += `, ${it.batch}`;
        }
        if (it.exp && prod.exp === '-') {
          prod.exp = it.exp;
        }
        if (it.company && prod.company === '-') {
          prod.company = it.company;
        }
        if (p.supplierName && (!prod.supplier || prod.supplier === 'Unknown')) {
          prod.supplier = p.supplierName;
        } else if (p.supplierName && prod.supplier && !prod.supplier.split(', ').includes(p.supplierName)) {
          prod.supplier += `, ${p.supplierName}`;
        }
      });
    });

    (allSales || []).forEach(s => {
      (s.items || []).forEach(it => {
        if (!it.productName) return;
        const nameClean = it.productName.trim();
        const packClean = getPacking(it);
        const nameKey = nameClean.toUpperCase();
        const packKey = packClean.toUpperCase();
        const exactKey = `${nameKey}||${packKey}`;
        const saleQty = parseFloat(it.qty) || 0;
        if (saleQty <= 0) return;

        if (packKey && productStockMap.has(exactKey)) {
          productStockMap.get(exactKey).soldQty += saleQty;
        } else if (productStockMap.has(exactKey)) {
          productStockMap.get(exactKey).soldQty += saleQty;
        } else {
          // If packing was not specified in the sale item or doesn't match an exact key,
          // deduct from items matching the medicine name that have available stock
          let remainingSale = saleQty;
          for (const [k, prod] of productStockMap.entries()) {
            if (k.startsWith(`${nameKey}||`)) {
              const currentAvail = Math.max(0, prod.purchasedQty - prod.soldQty - prod.returnedQty);
              if (currentAvail > 0 && remainingSale > 0) {
                const deduct = Math.min(currentAvail, remainingSale);
                prod.soldQty += deduct;
                remainingSale -= deduct;
              }
            }
          }
          if (remainingSale > 0) {
            for (const [k, prod] of productStockMap.entries()) {
              if (k.startsWith(`${nameKey}||`)) {
                prod.soldQty += remainingSale;
                break;
              }
            }
          }
        }
      });
    });

    (creditNotes || []).forEach(cn => {
      (cn.items || []).forEach(it => {
        if (!it.productName) return;
        const nameClean = it.productName.trim();
        const packClean = getPacking(it);
        const nameKey = nameClean.toUpperCase();
        const packKey = packClean.toUpperCase();
        const exactKey = `${nameKey}||${packKey}`;
        const retQty = parseFloat(it.qty) || 0;
        if (retQty <= 0) return;

        if (packKey && productStockMap.has(exactKey)) {
          productStockMap.get(exactKey).returnedQty += retQty;
        } else if (productStockMap.has(exactKey)) {
          productStockMap.get(exactKey).returnedQty += retQty;
        } else {
          let remainingRet = retQty;
          for (const [k, prod] of productStockMap.entries()) {
            if (k.startsWith(`${nameKey}||`)) {
              const currentAvail = Math.max(0, prod.purchasedQty - prod.soldQty - prod.returnedQty);
              if (currentAvail > 0 && remainingRet > 0) {
                const deduct = Math.min(currentAvail, remainingRet);
                prod.returnedQty += deduct;
                remainingRet -= deduct;
              }
            }
          }
          if (remainingRet > 0) {
            for (const [k, prod] of productStockMap.entries()) {
              if (k.startsWith(`${nameKey}||`)) {
                prod.returnedQty += remainingRet;
                break;
              }
            }
          }
        }
      });
    });

    let uniqueInStockCount = 0;
    let totalStockValue = 0;
    let sumAvailableQty = 0;
    const itemsList = [];

    productStockMap.forEach((prod) => {
      prod.availableQty = Math.max(0, prod.purchasedQty - prod.soldQty - prod.returnedQty);
      if (prod.availableQty > 0) {
        uniqueInStockCount++;
        totalStockValue += (prod.availableQty * prod.rate);
        sumAvailableQty += prod.availableQty;
        itemsList.push(prod);
      }
    });

    itemsList.sort((a, b) => b.availableQty - a.availableQty);

    return {
      totalAvailableQty: sumAvailableQty > 0 ? sumAvailableQty : totalAvailableQty,
      uniqueInStockCount,
      totalStockValue,
      itemsList
    };
  }, [allPurchases, allSales, creditNotes]);

  // Unify saved Credit Notes with auto-synced Near Expiry Stock Returns
  const unifiedCreditNotes = React.useMemo(() => {
    const list = [...(creditNotes || [])];
    const existingParties = new Set(list.map(cn => (cn.supplierName || '').trim().toUpperCase()));

    const today = new Date();
    const twoMonthsLater = new Date();
    twoMonthsLater.setMonth(today.getMonth() + 2);
    twoMonthsLater.setDate(28);

    const partyNearExpiryMap = new Map();
    (allPurchases || []).forEach(p => {
      const supplier = (p.supplierName || p.supplier || '').trim();
      if (!supplier) return;
      const key = supplier.toUpperCase();

      if (p.items && Array.isArray(p.items)) {
        p.items.forEach(item => {
          if (item.exp) {
            const parts = item.exp.split('/');
            if (parts.length === 2) {
              const month = parseInt(parts[0], 10) - 1;
              let year = parseInt(parts[1], 10);
              if (year < 100) year += 2000;
              const expDate = new Date(year, month + 1, 0);

              if (expDate <= twoMonthsLater) {
                const qty = parseInt(item.qty, 10) || 0;
                const rate = parseFloat(item.rate || item.mrp || 0) || 0;
                const amount = parseFloat(item.amount) || (qty * rate);

                if (!partyNearExpiryMap.has(key)) {
                  partyNearExpiryMap.set(key, {
                    supplierName: supplier,
                    items: [],
                    totalAmount: 0,
                    totalQty: 0
                  });
                }
                const entry = partyNearExpiryMap.get(key);
                entry.totalAmount += amount;
                entry.totalQty += qty;
                entry.items.push(item);
              }
            }
          }
        });
      }
    });

    partyNearExpiryMap.forEach((data, key) => {
      if (!existingParties.has(key) && data.totalAmount > 0) {
        list.push({
          id: `NE-CN-${key}`,
          creditNoteNo: `CN-EXP-${key.slice(0, 4)}`,
          supplierName: data.supplierName,
          date: new Date().toISOString().split('T')[0],
          totalAmount: data.totalAmount,
          totalQty: data.totalQty,
          totalItems: data.items.length,
          items: data.items,
          reason: 'Near Expiry Stock Return (Auto-Synced)',
          isAutoSynced: true
        });
      }
    });

    return list.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [creditNotes, allPurchases]);

  const openReturnModal = (supplier = '', specificBill = null) => {
    let partyName = supplier;
    if (!partyName && filteredStockists.length > 0) {
      partyName = filteredStockists[0].name;
    } else if (!partyName && allPurchases.length > 0) {
      partyName = allPurchases[0].supplierName;
    }
    setReturnSupplierName(partyName || '');
    buildReturnItemList(partyName || '', specificBill);
    setShowReturnModal(true);
  };

  const buildReturnItemList = (partyName, specificBill = null) => {
    if (!partyName) {
      setReturnItems([]);
      return;
    }
    const partyNameUpper = partyName.trim().toUpperCase();

    let relevantBills = allPurchases || [];
    if (specificBill) {
      relevantBills = [specificBill];
    } else {
      relevantBills = relevantBills.filter(p => (p.supplierName || p.supplier || '').trim().toUpperCase() === partyNameUpper);
    }

    const itemsMap = new Map();

    relevantBills.forEach(bill => {
      (bill.items || []).forEach(item => {
        if (!item.productName) return;
        const packStr = (item.packing || item.package || item.pack || item.packSize || item.pkg || item.strip || '').trim();
        const key = item.productName.trim().toUpperCase() + '|' + packStr.toUpperCase() + '|' + (item.batch || '').trim().toUpperCase();
        const purchasedQty = parseFloat(item.qty) || 0;
        const rate = parseFloat(item.rate || item.mrp || 0) || 0;

        if (!itemsMap.has(key)) {
          itemsMap.set(key, {
            productName: item.productName,
            packing: packStr || '-',
            company: item.mfg || item.company || '',
            batch: item.batch || '-',
            exp: item.exp || '-',
            purchasedQty: 0,
            soldQty: 0,
            alreadyReturnedQty: 0,
            rate: rate,
            invoiceNo: bill.invoiceNo || bill.vchNo || '-'
          });
        }
        itemsMap.get(key).purchasedQty += purchasedQty;
      });
    });

    (allSales || []).forEach(sale => {
      (sale.items || []).forEach(item => {
        if (!item.productName) return;
        const pNameUpper = item.productName.trim().toUpperCase();
        const pPackUpper = (item.packing || item.package || item.pack || item.packSize || item.pkg || item.strip || '').trim().toUpperCase();
        const bUpper = (item.batch || '').trim().toUpperCase();

        itemsMap.forEach((val, key) => {
          const [nameKey, packKey, batchKey] = key.split('|');
          if (nameKey === pNameUpper && (!pPackUpper || !packKey || packKey === '-' || pPackUpper === packKey) && (!batchKey || batchKey === '-' || !bUpper || bUpper === batchKey)) {
            val.soldQty += (parseFloat(item.qty) || 0);
          }
        });
      });
    });

    (creditNotes || []).forEach(cn => {
      if ((cn.supplierName || '').trim().toUpperCase() === partyNameUpper) {
        (cn.items || []).forEach(item => {
          if (!item.productName) return;
          const pNameUpper = item.productName.trim().toUpperCase();
          const pPackUpper = (item.packing || item.package || item.pack || item.packSize || item.pkg || item.strip || '').trim().toUpperCase();
          const bUpper = (item.batch || '').trim().toUpperCase();

          itemsMap.forEach((val, key) => {
            const [nameKey, packKey, batchKey] = key.split('|');
            if (nameKey === pNameUpper && (!pPackUpper || !packKey || packKey === '-' || pPackUpper === packKey) && (!batchKey || batchKey === '-' || !bUpper || bUpper === batchKey)) {
              val.alreadyReturnedQty += (parseFloat(item.qty) || 0);
            }
          });
        });
      }
    });

    const resultRows = [];
    let hasExpired = false;
    itemsMap.forEach((val) => {
      const availStock = Math.max(0, val.purchasedQty - val.soldQty - val.alreadyReturnedQty);
      // Once stock is returned or has 0 available stock, it should NOT show up again!
      if (availStock <= 0) return;

      const expStatus = checkExpiryStatus(val.exp);
      if (expStatus.isExpired) {
        hasExpired = true;
      }
      resultRows.push({
        ...val,
        expiryInfo: expStatus,
        selected: expStatus.isExpired,
        availableStock: availStock,
        returnQty: availStock,
        amount: availStock * val.rate
      });
    });

    if (hasExpired) {
      setReturnFilterTab('EXPIRED');
      setReturnReason('Expired Medicine Stock Return to Party / Expiry Return');
    } else {
      setReturnFilterTab('ALL');
      setReturnReason('Stock Return to Party');
    }

    setReturnItems(resultRows);
  };

  const handleSaveStockReturn = async () => {
    const selectedRows = returnItems.filter(r => r.selected && (parseFloat(r.returnQty) || 0) > 0);
    if (selectedRows.length === 0) {
      alert("Please select at least one product with a return quantity greater than 0.");
      return;
    }

    if (!returnSupplierName) {
      alert("Please select a supplier party.");
      return;
    }

    const totalAmt = selectedRows.reduce((sum, r) => sum + (parseFloat(r.returnQty) || 0) * (parseFloat(r.rate) || 0), 0);
    const totalQty = selectedRows.reduce((sum, r) => sum + (parseFloat(r.returnQty) || 0), 0);
    const cnNo = `CN-${Math.floor(1000 + Math.random() * 9000)}`;
    const dateStr = new Date().toISOString().split('T')[0];

    const creditNoteData = {
      creditNoteNo: cnNo,
      supplierName: returnSupplierName,
      date: dateStr,
      totalAmount: totalAmt,
      totalQty: totalQty,
      totalItems: selectedRows.length,
      items: selectedRows.map(r => ({
        productName: r.productName,
        packing: r.packing || '',
        company: r.company,
        batch: r.batch,
        exp: r.exp,
        qty: parseFloat(r.returnQty) || 0,
        rate: parseFloat(r.rate) || 0,
        amount: (parseFloat(r.returnQty) || 0) * (parseFloat(r.rate) || 0),
        invoiceNo: r.invoiceNo
      })),
      reason: returnReason || 'Unsold Stock Return to Party'
    };

    try {
      setIsSubmittingReturn(true);
      let saved = false;
      try {
        const res = await fetch(apiUrl('/api/credit-notes'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(creditNoteData)
        });
        if (res.ok) {
          saved = true;
        }
      } catch (e) {
        console.error('Failed to save credit note:', e);
      }

      if (!saved) {
        throw new Error("Could not connect to backend server");
      }

      // Optimistically update creditNotes so stock updates instantly and does not show again!
      setCreditNotes(prev => [creditNoteData, ...prev]);

      alert(`✅ Stock Return for ${returnSupplierName} saved successfully!\nReturned ${totalQty} units (₹${totalAmt.toFixed(2)}). Stock is cleared and will no longer show in Sales dropdown.`);
      setShowReturnModal(false);
      fetchData(true);
    } catch (err) {
      console.error("Error saving return to party:", err);
      alert("Failed to save stock return: " + err.message);
    } finally {
      setIsSubmittingReturn(false);
    }
  };

  return (
    <div className="w-full space-y-4 md:space-y-6 animate-in fade-in duration-500 p-2 sm:p-4 md:p-6">
      
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/dashboard')}
            className="p-2 bg-white rounded-xl shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors text-slate-600"
            title="Back to Dashboard"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
          </button>
          <div className="w-1.5 h-8 md:h-10 rounded-full bg-blue-600 shadow-sm"></div>
          <div>
            <h2 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              <span>Stockist Purchases & Management</span>
              <span className="px-2.5 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">
                {activeTab === 'bills' ? `${filteredPurchases.length} Bills` : `${filteredStockists.length} Stockists`}
              </span>
            </h2>
            <p className="text-xs text-slate-500 font-medium">Firestore Auto-Sync • खरेदी बिले व स्टॉकिस्ट मास्टर व्यवस्थापन</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Live Auto-Sync Status Indicator */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs font-bold">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <span>Live Auto-Sync</span>
            <button 
              onClick={() => fetchData()}
              disabled={loading || isRefreshing}
              title="Refresh Data"
              className="ml-1 text-emerald-700 hover:text-emerald-900 transition-transform active:scale-90"
            >
              <svg className={`w-3.5 h-3.5 ${isRefreshing || loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
            </button>
          </div>

          {/* Full Screen Toggle Button */}
          <button 
            onClick={toggleFullscreen}
            className="px-3.5 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-xl font-bold text-xs border border-slate-200 shadow-sm transition-all flex items-center gap-1.5 active:scale-95"
            title={isFullscreen ? "Exit Fullscreen" : "Full Screen View"}
          >
            {isFullscreen ? (
              <>
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 9L4 4m0 0l5 0m-5 0l0 5m11 0l5-5m0 0l-5 0m5 0l0 5M9 15l-5 5m0 0l5 0m-5 0l0-5m11 0l5 5m0 0l-5 0m5 0l0-5" />
                </svg>
                <span>Exit Fullscreen</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                <span>Full Screen</span>
              </>
            )}
          </button>

          <button 
            onClick={() => openReturnModal()}
            className="px-3.5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold text-xs shadow-md shadow-teal-600/20 transition-all flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path></svg>
            ↩️ Return Stock to Party
          </button>

          <button 
            onClick={() => setShowAddStockistModal(true)}
            className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs shadow-md shadow-emerald-600/20 transition-all flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path></svg>
            + Add Stockist
          </button>
        </div>
      </div>

      {/* Tabs Switcher */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('bills')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${activeTab === 'bills' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
          Inward Bills ({filteredPurchases.length})
        </button>

        <button
          onClick={() => setActiveTab('directory')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${activeTab === 'directory' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
          Stockist Directory ({unifiedStockists.length})
        </button>

        <button
          onClick={() => setActiveTab('credit-notes')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${activeTab === 'credit-notes' ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
        >
          <span>💳 Credit Notes / Returns ({unifiedCreditNotes.length})</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        
        {/* Total Stockist Purchases */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Purchases</span>
            <h3 className="text-2xl font-black text-slate-800 mt-1">₹{totalAmount.toFixed(2)}</h3>
            <p className="text-[10px] text-slate-500 mt-0.5">{filteredPurchases.length} Total Inward Bills</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
          </div>
        </div>

        {/* Credit Purchases (Due) */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-amber-600 uppercase tracking-wider">Credit Purchases</span>
            <h3 className="text-2xl font-black text-amber-700 mt-1">₹{creditAmount.toFixed(2)}</h3>
            <p className="text-[10px] text-amber-600/80 mt-0.5">Pending party credit</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-100">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          </div>
        </div>

        {/* Cash Purchases */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider">Cash Purchases</span>
            <h3 className="text-2xl font-black text-emerald-700 mt-1">₹{cashAmount.toFixed(2)}</h3>
            <p className="text-[10px] text-emerald-600/80 mt-0.5">Paid instantly</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          </div>
        </div>

        {/* Available Stock Card */}
        <div 
          onClick={() => setShowStockModal(true)}
          className="bg-white rounded-2xl p-4 border border-teal-200/90 shadow-sm flex items-center justify-between bg-gradient-to-br from-white to-teal-50/40 cursor-pointer hover:border-teal-400 hover:shadow-md transition-all group"
          title="Click to view detailed available stock list"
        >
          <div>
            <span className="text-[11px] font-bold text-teal-700 uppercase tracking-wider group-hover:text-teal-900 transition-colors">Available Stock 🔍</span>
            <h3 className="text-2xl font-black text-teal-800 mt-1">{availableStockMetrics.totalAvailableQty.toLocaleString()} Units</h3>
            <p className="text-[10px] text-teal-600/90 font-bold mt-0.5">{availableStockMetrics.uniqueInStockCount} Medicines • Click for details</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-teal-100/70 text-teal-700 flex items-center justify-center border border-teal-200 shadow-xs group-hover:scale-110 transition-transform">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>
          </div>
        </div>

        {/* Stockist Count */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-purple-600 uppercase tracking-wider">Stockist Count</span>
            <h3 className="text-2xl font-black text-purple-800 mt-1">{unifiedStockists.length}</h3>
            <p className="text-[10px] text-purple-600/80 mt-0.5">Live Stockists Master</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center border border-purple-100">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
          </div>
        </div>

      </div>

      {/* Filter Control Bar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          
          {/* Text Search */}
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Stockist, Invoice No, Medicine..."
                className="w-full pl-8 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 outline-none focus:border-blue-500 transition-colors"
              />
              <svg className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 text-xs font-bold">×</button>
              )}
            </div>
          </div>

          {/* Supplier Dropdown */}
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Stockist / Party</label>
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className="w-full p-2 text-xs bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 outline-none focus:border-blue-500 transition-colors"
            >
              <option value="ALL">All Stockists ({suppliersList.length})</option>
              {suppliersList.map((sup, idx) => (
                <option key={idx} value={sup}>{sup}</option>
              ))}
            </select>
          </div>

          {/* Payment Type */}
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Payment Mode</label>
            <select
              value={selectedPaymentType}
              onChange={(e) => setSelectedPaymentType(e.target.value)}
              className="w-full p-2 text-xs bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 outline-none focus:border-blue-500 transition-colors"
            >
              <option value="ALL">All Modes (Credit & Cash)</option>
              <option value="Credit">Credit Bills Only</option>
              <option value="Cash">Cash Bills Only</option>
            </select>
          </div>

          {/* Date Filter */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">From Date</label>
              <input 
                type="date" 
                value={fromDate} 
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full p-2 text-xs bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">To Date</label>
              <input 
                type="date" 
                value={toDate} 
                onChange={(e) => setToDate(e.target.value)}
                className="w-full p-2 text-xs bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 outline-none focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

        </div>

        {(searchQuery || selectedSupplier !== 'ALL' || selectedPaymentType !== 'ALL' || fromDate || toDate) && (
          <div className="flex justify-end pt-1">
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedSupplier('ALL');
                setSelectedPaymentType('ALL');
                setFromDate('');
                setToDate('');
              }}
              className="text-xs font-bold text-rose-600 hover:text-rose-700 transition-colors flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* Main Stockist Purchases Table / Directory */}
      {activeTab === 'bills' ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-xs text-slate-400 mt-2 font-medium">Loading stockist purchases...</p>
            </div>
          ) : filteredPurchases.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold border-b border-slate-200">
                    <th className="p-3.5">Date</th>
                    <th className="p-3.5">Stockist / Supplier Name</th>
                    <th className="p-3.5">Invoice No</th>
                    <th className="p-3.5">Payment</th>
                    <th className="p-3.5 text-center">Items</th>
                    <th className="p-3.5 text-right">Net Amount</th>
                    <th className="p-3.5 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {filteredPurchases.map((purchase, index) => {
                    const billAmt = purchase.netAmount || purchase.totalAmount || purchase.grossAmount || 0;
                    const itemsCount = (purchase.items && purchase.items.length) || 0;
                    const totalBillQty = (purchase.items || []).reduce((sum, it) => sum + (parseFloat(it.qty) || 0), 0) || purchase.totalQty || itemsCount;
                    const itemsPreview = (purchase.items || []).slice(0, 2).map(it => it.productName).join(', ');
                    const billExpiredCount = (purchase.items || []).filter(it => checkExpiryStatus(it.exp).isExpired).length;

                    return (
                      <tr 
                        key={purchase.id || index}
                        className="hover:bg-blue-50/40 transition-colors cursor-pointer group"
                        onClick={() => setSelectedPurchase(purchase)}
                      >
                        <td className="p-3.5 text-slate-600 font-semibold whitespace-nowrap">
                          {purchase.date ? new Date(purchase.date).toLocaleDateString('en-GB') : '-'}
                        </td>
                        <td className="p-3.5">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 font-bold flex items-center justify-center shrink-0 text-xs">
                              🏢
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-bold text-slate-800 uppercase group-hover:text-blue-700 transition-colors">
                                  {purchase.supplierName || 'Unknown Party'}
                                </span>
                                <span className="px-1.5 py-0.2 bg-teal-50 text-teal-700 border border-teal-200 text-[9px] font-bold rounded">
                                  Purchased by: {purchase.buyerName || purchase.pharmacyName || 'Sai Pharmacy'}
                                </span>
                              </div>
                              {itemsCount > 0 && (
                                <span className="text-[10px] text-slate-400 block truncate max-w-xs md:max-w-md lg:max-w-xl">
                                  {itemsPreview} {itemsCount > 2 ? `+${itemsCount - 2} more` : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3.5 font-bold text-slate-600">
                          {purchase.invoiceNo || '-'}
                        </td>
                        <td className="p-3.5">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            purchase.paymentType === 'Cash' 
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                              : 'bg-amber-100 text-amber-800 border border-amber-200'
                          }`}>
                            {purchase.paymentType || 'Credit'}
                          </span>
                        </td>
                        <td className="p-3.5 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className="px-2.5 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 font-bold rounded-full text-[11px] shadow-xs flex items-center gap-1">
                              <span>{itemsCount} {itemsCount === 1 ? 'Product' : 'Products'}</span>
                              <span className="text-blue-300 font-normal">|</span>
                              <span className="font-black text-emerald-700">{totalBillQty} Qty</span>
                            </span>
                            {billExpiredCount > 0 && (
                              <span className="px-2 py-0.5 bg-rose-100 text-rose-700 border border-rose-300 font-black rounded-full text-[10px]">
                                ⚠️ {billExpiredCount} Expired
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3.5 text-right font-black text-slate-900 text-sm">
                          ₹{typeof billAmt === 'number' ? billAmt.toFixed(2) : billAmt}
                        </td>
                        <td className="p-3.5 text-center">
                          <div className="flex items-center justify-center gap-1.5 flex-wrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPurchase(purchase);
                              }}
                              className="px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white rounded-lg font-bold text-xs transition-all border border-blue-200 cursor-pointer"
                              title="View full bill details"
                            >
                              View Bill
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openReturnModal(purchase.supplierName, purchase);
                              }}
                              className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all border cursor-pointer flex items-center gap-1 ${
                                billExpiredCount > 0
                                  ? 'bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-600 hover:text-white'
                                  : 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-600 hover:text-white'
                              }`}
                              title={billExpiredCount > 0 ? "Return expired medicines from this bill" : "Return unsold stock of this bill to party"}
                            >
                              {billExpiredCount > 0 ? "⚠️ Return Expired" : "Return Stock"}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePurchase(purchase);
                              }}
                              className="px-2.5 py-1 bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white rounded-lg font-bold text-xs transition-all border border-rose-200 flex items-center gap-1 cursor-pointer"
                              title="Delete this purchase bill"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-16 text-center text-slate-400">
              <svg className="w-12 h-12 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
              <p className="font-bold text-sm text-slate-600">No stockist purchases match your filters.</p>
              <p className="text-xs text-slate-400 mt-0.5">Try resetting search or adjusting date filters.</p>
            </div>
          )}
        </div>
      ) : activeTab === 'directory' ? (
        /* Stockist Directory Table */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex justify-between items-center">
            <h3 className="font-bold text-sm text-slate-800 flex items-center gap-2">
              <span>Stockist Directory (Auto-Synced Master)</span>
              <span className="text-xs text-slate-400 font-normal">({filteredStockists.length} stockists)</span>
            </h3>
          </div>

          {filteredStockists.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <p className="font-bold text-sm">No stockists found in collection</p>
              <button 
                onClick={() => setShowAddStockistModal(true)}
                className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold"
              >
                Add First Stockist
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold border-b border-slate-200">
                    <th className="p-3.5">Stockist Name</th>
                    <th className="p-3.5">Contact / Phone</th>
                    <th className="p-3.5">GST No</th>
                    <th className="p-3.5">Address</th>
                    <th className="p-3.5 text-center">Total Bills</th>
                    <th className="p-3.5 text-right">Total Purchases</th>
                    <th className="p-3.5 text-center">Last Purchase</th>
                    <th className="p-3.5 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {filteredStockists.map((stk, idx) => (
                    <tr key={stk.id || idx} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3.5 font-bold text-slate-800 uppercase">
                        <div className="flex items-center gap-2">
                          <span className="text-base">🏢</span>
                          <span>{stk.name}</span>
                        </div>
                      </td>
                      <td className="p-3.5 text-slate-600 font-semibold">
                        {stk.phone || stk.mobile || '-'}
                      </td>
                      <td className="p-3.5 font-semibold text-slate-700">
                        {stk.gst || stk.gstNo || '-'}
                      </td>
                      <td className="p-3.5 text-slate-500 truncate max-w-[200px] md:max-w-xs lg:max-w-md">
                        {stk.address || '-'}
                      </td>
                      <td className="p-3.5 text-center font-bold text-blue-600">
                        {stk.totalBills || 0}
                      </td>
                      <td className="p-3.5 text-right font-black text-slate-800">
                        ₹{parseFloat(stk.totalPurchases || 0).toFixed(2)}
                      </td>
                      <td className="p-3.5 text-center text-slate-500 whitespace-nowrap">
                        {stk.lastPurchaseDate ? new Date(stk.lastPurchaseDate).toLocaleDateString('en-GB') : '-'}
                      </td>
                      <td className="p-3.5 text-center">
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          <button
                            onClick={() => {
                              setSelectedSupplier(stk.name);
                              setActiveTab('bills');
                            }}
                            className="px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white rounded-lg font-bold text-xs transition-all border border-blue-200 cursor-pointer"
                          >
                            View Bills
                          </button>
                          <button
                            onClick={() => openReturnModal(stk.name)}
                            className="px-2.5 py-1 bg-teal-50 text-teal-700 hover:bg-teal-600 hover:text-white rounded-lg font-bold text-xs transition-all border border-teal-200 cursor-pointer"
                            title="Return unsold stock to this party"
                          >
                            ↩️ Return Stock
                          </button>
                          <button
                            onClick={() => handleDeleteStockist(stk)}
                            className="px-2.5 py-1 bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white rounded-lg font-bold text-xs transition-all border border-rose-200 flex items-center gap-1 cursor-pointer"
                            title="Delete this stockist party"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Credit Notes & Stock Returns Table */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-sm text-slate-800 flex items-center gap-2">
                <span>💳 Credit Notes & Stock Returns (Auto-Synced)</span>
                <span className="text-xs text-slate-400 font-normal">({unifiedCreditNotes.length} credit notes)</span>
              </h3>
              <p className="text-xs text-slate-400 font-medium">Stock returned to suppliers from near expiry & expired inventory</p>
            </div>
            <div className="px-3 py-1.5 bg-teal-100 text-teal-800 font-black text-xs rounded-xl border border-teal-200">
              Total Credited Returns: ₹{unifiedCreditNotes.reduce((acc, c) => acc + (parseFloat(c.totalAmount || 0) || 0), 0).toFixed(2)}
            </div>
          </div>

          {unifiedCreditNotes.length === 0 ? (
            <div className="p-16 text-center text-slate-400 font-bold text-xs">
              <span className="text-3xl block mb-2">💳</span>
              <p className="text-slate-600 font-bold text-sm">No Credit Notes or Stock Returns created yet.</p>
              <p className="text-slate-400 mt-1">Go to Near Expiry section to return expired stock to supplier parties.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold border-b border-slate-200">
                    <th className="p-3.5">Credit Note No</th>
                    <th className="p-3.5">Return Date</th>
                    <th className="p-3.5">Supplier / Stockist Party</th>
                    <th className="p-3.5">Items & Quantity</th>
                    <th className="p-3.5 text-right">Credited Return Value (₹)</th>
                    <th className="p-3.5 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {unifiedCreditNotes.map((cn, idx) => (
                    <tr key={cn.id || idx} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3.5 font-black text-teal-700">
                        <span className="px-2 py-0.5 bg-teal-50 border border-teal-200 rounded inline-flex items-center gap-1">
                          {cn.creditNoteNo || 'CN-MEMO'}
                          {cn.isAutoSynced && <span className="text-[9px] bg-teal-100 text-teal-800 px-1 rounded font-bold">Near Expiry</span>}
                        </span>
                      </td>
                      <td className="p-3.5 font-bold text-slate-600">{cn.date}</td>
                      <td className="p-3.5 font-black text-slate-800 uppercase">{cn.supplierName}</td>
                      <td className="p-3.5 text-slate-600">
                        <div className="font-bold text-slate-800">{cn.totalItems || (cn.items ? cn.items.length : 0)} Items ({cn.totalQty || 0} Units)</div>
                        <div className="text-[10px] text-slate-400 truncate max-w-md lg:max-w-xl">
                          {cn.items ? cn.items.map(i => i.productName).join(', ') : (cn.reason || '-')}
                        </div>
                      </td>
                      <td className="p-3.5 text-right font-black text-teal-700 text-sm">
                        ₹{parseFloat(cn.totalAmount || 0).toFixed(2)}
                      </td>
                      <td className="p-3.5 text-center">
                        <button
                          onClick={() => handleDeleteCreditNote(cn)}
                          className="px-2.5 py-1 bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white rounded-lg font-bold text-xs transition-all border border-rose-200 flex items-center gap-1 cursor-pointer mx-auto"
                          title="Delete this return record / credit note"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Stockist Bill Detail Modal */}
      {selectedPurchase && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-3 md:p-6 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-700 via-blue-800 to-indigo-900 text-white p-5 flex justify-between items-start shrink-0">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-white text-[11px] font-bold uppercase tracking-wider">
                    Stockist Inward Invoice
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full bg-teal-400 text-teal-950 text-[11px] font-bold uppercase tracking-wider">
                    Billed To: {selectedPurchase.buyerName || selectedPurchase.pharmacyName || 'Sai Pharmacy'}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${selectedPurchase.paymentType === 'Cash' ? 'bg-emerald-400 text-emerald-950' : 'bg-amber-400 text-amber-950'}`}>
                    {selectedPurchase.paymentType || 'Credit'}
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl font-black mt-1 uppercase tracking-tight">
                  {selectedPurchase.supplierName || 'Unknown Party'}
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-blue-100 font-medium">
                  <span>📄 Invoice No: <strong className="text-white">{selectedPurchase.invoiceNo || '-'}</strong></span>
                  <span>•</span>
                  <span>📅 Date: <strong className="text-white">{selectedPurchase.date ? new Date(selectedPurchase.date).toLocaleDateString('en-GB') : '-'}</strong></span>
                  {selectedPurchase.supplierPhone && (
                    <>
                      <span>•</span>
                      <span>📞 {selectedPurchase.supplierPhone}</span>
                    </>
                  )}
                  {selectedPurchase.supplierGst && (
                    <>
                      <span>•</span>
                      <span>GST: {selectedPurchase.supplierGst}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {selectedPurchase.billImage && (
                  <button
                    type="button"
                    onClick={() => setShowBillPhotoInStockist(!showBillPhotoInStockist)}
                    className="px-3 py-1.5 bg-teal-500/30 hover:bg-teal-500/50 text-teal-200 border border-teal-400/40 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-colors shadow-xs"
                    title="Toggle original bill photo"
                  >
                    <span>📷 {showBillPhotoInStockist ? 'Hide Photo' : 'View Bill Photo'}</span>
                  </button>
                )}
                <button 
                  onClick={() => {
                    setSelectedPurchase(null);
                    setShowBillPhotoInStockist(false);
                  }} 
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
            </div>

            {/* Modal Items Body */}
            <div className="flex-1 overflow-auto p-4 md:p-6 bg-slate-50">
              {/* Optional Bill Photo Preview Card */}
              {showBillPhotoInStockist && selectedPurchase.billImage && (
                <div className="bg-slate-900 border-2 border-teal-500/60 rounded-2xl overflow-hidden shadow-xl mb-4 animate-in zoom-in-95 duration-200">
                  <div className="p-3 bg-slate-950 text-white flex items-center justify-between border-b border-teal-600/40">
                    <span className="text-xs font-black uppercase tracking-wider text-teal-300 flex items-center gap-1.5">
                      <span>📷 Original Uploaded Bill Photo</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowBillPhotoInStockist(false)}
                      className="text-xs text-slate-400 hover:text-white px-2 py-0.5 rounded bg-slate-800"
                    >
                      ✕ Close Photo
                    </button>
                  </div>
                  <div className="p-4 flex items-center justify-center bg-slate-950/80 max-h-[450px] overflow-auto">
                    <img 
                      src={selectedPurchase.billImage} 
                      alt="Bill Document"
                      className="max-h-[400px] max-w-full rounded-lg shadow-md border border-slate-700 object-contain"
                    />
                  </div>
                </div>
              )}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-4">
                <div className="p-3.5 bg-slate-100/70 border-b border-slate-200 font-bold text-xs text-slate-700 uppercase tracking-wider flex items-center justify-between flex-wrap gap-2">
                  <span>Purchased Items Breakdown</span>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-lg font-black text-xs">
                      Total Items: {(selectedPurchase.items && selectedPurchase.items.length) || selectedPurchase.totalItems || 0}
                    </span>
                    <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-lg font-black text-xs">
                      Total Qty: {(selectedPurchase.items || []).reduce((sum, it) => sum + (parseFloat(it.qty) || 0), 0) || selectedPurchase.totalQty || 0}
                    </span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold border-b border-slate-200">
                        <th className="p-3">#</th>
                        <th className="p-3">Product Name & Mfg</th>
                        <th className="p-3">Batch</th>
                        <th className="p-3">Exp</th>
                        <th className="p-3 text-center">Qty</th>
                        <th className="p-3 text-center">Free</th>
                        <th className="p-3 text-right">Rate (₹)</th>
                        <th className="p-3 text-right">MRP (₹)</th>
                        <th className="p-3 text-right">GST%</th>
                        <th className="p-3 text-right bg-teal-50 text-teal-800" title="Rate with GST included">Cost Rate (₹)</th>
                        <th className="p-3 text-right">Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(selectedPurchase.items || []).map((item, i) => (
                        <tr key={i} className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-3 text-slate-400 font-bold">{i + 1}</td>
                          <td className="p-3 font-bold text-slate-800 uppercase">
                            <div>{item.productName}</div>
                            {item.mfg && (
                              <span className="text-[10px] text-slate-400 lowercase font-medium tracking-normal block">
                                mfg: {item.mfg}
                              </span>
                            )}
                          </td>
                          <td className="p-3 font-semibold text-slate-600">{item.batch || '-'}</td>
                          <td className="p-3 text-slate-600">{item.exp || '-'}</td>
                          <td className="p-3 text-center font-bold text-blue-600 bg-blue-50/30">{item.qty || 0}</td>
                          <td className="p-3 text-center text-slate-500">{item.free || item.freeQty || item.sch || 0}</td>
                          <td className="p-3 text-right text-slate-700 font-semibold">₹{parseFloat(item.rate || 0).toFixed(2)}</td>
                          <td className="p-3 text-right text-slate-500">₹{parseFloat(item.mrp || 0).toFixed(2)}</td>
                          <td className="p-3 text-right text-slate-500">{item.gst || item.gstRate || 0}%</td>
                          <td className="p-3 text-right font-black text-teal-700 bg-teal-50/40">₹{(parseFloat(item.costRate || item.costPrice) || (parseFloat(item.rate || 0) * (1 + (parseFloat(item.gst || item.gstRate || 0)/100)))).toFixed(2)}</td>
                          <td className="p-3 text-right font-black text-slate-900">
                            ₹{parseFloat(item.amount || ((parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0))).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bill Financial Summary - Comprehensive Breakdown */}
              <div className="p-4 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-950 text-white rounded-2xl shadow-lg border border-teal-500/30 space-y-3">
                <div className="flex items-center justify-between pb-2 border-b border-white/10">
                  <span className="text-xs font-black uppercase tracking-wider text-teal-300">
                    🧾 Complete Bill Financial Summary (हिशोब)
                  </span>
                  <div className="flex items-center gap-1 text-[11px] text-slate-300">
                    <span>Payment:</span>
                    <strong className="text-white bg-white/10 px-2 py-0.5 rounded uppercase">{selectedPurchase.paymentType || 'Credit'}</strong>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  {/* Gross Amount */}
                  <div className="bg-white/5 p-2.5 rounded-xl border border-white/10">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Gross Amount (एकूण मूळ)</div>
                    <div className="text-base font-black text-white mt-0.5">
                      ₹{parseFloat(selectedPurchase.grossAmount || selectedPurchase.totalAmount || 0).toFixed(2)}
                    </div>
                  </div>

                  {/* Total Discount */}
                  <div className="bg-rose-500/10 p-2.5 rounded-xl border border-rose-500/20">
                    <div className="text-[10px] uppercase font-bold text-rose-300 flex justify-between">
                      <span>Discount (सूट)</span>
                      {parseFloat(selectedPurchase.cashDiscPercent || 0) > 0 && (
                        <span className="text-[9px] bg-rose-500/30 px-1 rounded">{selectedPurchase.cashDiscPercent}%</span>
                      )}
                    </div>
                    <div className="text-base font-black text-rose-400 mt-0.5">
                      {(() => {
                        const iDisc = parseFloat(selectedPurchase.itemDisc || 0);
                        const cDisc = parseFloat(selectedPurchase.cashDisc || 0);
                        const sDisc = parseFloat(selectedPurchase.schDisc || 0);
                        const tot = iDisc + cDisc + sDisc;
                        return '-₹' + tot.toFixed(2);
                      })()}
                    </div>
                    <div className="text-[9px] text-slate-400">Item + Cash + Scheme</div>
                  </div>

                  {/* Less Amount (२ पद्धती: GST आधी vs GST नंतर) */}
                  <div className="bg-amber-500/10 p-2.5 rounded-xl border border-amber-500/20">
                    <div className="text-[10px] uppercase font-bold text-amber-300 flex items-center justify-between">
                      <span>Less (कमी रक्कम)</span>
                      <span className="text-[8px] bg-amber-400/20 text-amber-200 px-1 rounded">
                        {selectedPurchase.lessMode === 'POST_TAX' ? 'GST नंतर' : selectedPurchase.lessMode === 'BOTH' ? '२ पद्धती' : 'GST आधी'}
                      </span>
                    </div>
                    <div className="text-base font-black text-amber-400 mt-0.5">
                      -₹{(parseFloat(selectedPurchase.lessAmount || 0) + parseFloat(selectedPurchase.postTaxLess || 0)).toFixed(2)}
                    </div>
                    <div className="text-[9px] text-slate-400">
                      {selectedPurchase.lessMode === 'POST_TAX' ? 'करोत्तर वजावट (Net Less)' : selectedPurchase.lessMode === 'BOTH' ? `आधी: ₹${parseFloat(selectedPurchase.lessAmount || 0).toFixed(1)} | नंतर: ₹${parseFloat(selectedPurchase.postTaxLess || 0).toFixed(1)}` : 'करपूर्व वजावट (Pre-tax)'}
                    </div>
                  </div>

                  {/* Taxable Amount */}
                  <div className="bg-blue-500/10 p-2.5 rounded-xl border border-blue-500/20">
                    <div className="text-[10px] uppercase font-bold text-blue-300">Taxable (करपात्र मूल्य)</div>
                    <div className="text-base font-black text-blue-300 mt-0.5">
                      ₹{parseFloat(selectedPurchase.taxableAmount || 0).toFixed(2)}
                    </div>
                    <div className="text-[9px] text-slate-400">Base before Tax</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10 text-xs">
                  {/* Total Tax (GST) */}
                  <div className="bg-emerald-500/10 p-2.5 rounded-xl border border-emerald-500/20">
                    <div className="text-[10px] uppercase font-bold text-emerald-300">Total Tax (GST / कर)</div>
                    <div className="text-base font-black text-emerald-400 mt-0.5">
                      +₹{parseFloat(selectedPurchase.totalTaxAmount || selectedPurchase.totalTax || 0).toFixed(2)}
                    </div>
                    <div className="text-[9px] text-slate-400">GST Applied</div>
                  </div>

                  {/* Round Off */}
                  <div className="bg-white/5 p-2.5 rounded-xl border border-white/10">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Round Off (राउंड ऑफ)</div>
                    <div className="text-base font-black text-slate-300 mt-0.5">
                      {parseFloat(selectedPurchase.roundOff || 0) >= 0 ? `+₹${parseFloat(selectedPurchase.roundOff || 0).toFixed(2)}` : `-₹${Math.abs(parseFloat(selectedPurchase.roundOff || 0)).toFixed(2)}`}
                    </div>
                    <div className="text-[9px] text-slate-400">Adjustment</div>
                  </div>

                  {/* Net Bill Amount */}
                  <div className="bg-gradient-to-r from-teal-500 to-emerald-600 p-2.5 rounded-xl shadow-lg border border-teal-300/40 text-white">
                    <div className="text-[10px] uppercase font-black text-teal-100">Net Bill Total (एकूण देय)</div>
                    <div className="text-xl font-black mt-0.5">
                      ₹{parseFloat(selectedPurchase.netAmount || selectedPurchase.totalAmount || selectedPurchase.grossAmount || 0).toFixed(2)}
                    </div>
                    <div className="text-[9px] text-teal-100 font-bold">Total To Pay</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-white border-t border-slate-200 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const billToEdit = selectedPurchase;
                    setSelectedPurchase(null);
                    navigate('/dashboard/purchases', { state: { editBillId: billToEdit.id, editBill: billToEdit } });
                  }}
                  className="px-4 py-2 bg-amber-50 hover:bg-amber-500 text-amber-800 hover:text-white border border-amber-300 rounded-xl font-black text-xs transition-colors flex items-center gap-1.5 shadow-xs cursor-pointer"
                  title="Edit this purchase bill (F3)"
                >
                  <span className="bg-amber-600 text-white px-1.5 py-0.5 rounded text-[10px] font-black">F3</span>
                  ✏️ Edit Bill
                </button>
                <button
                  onClick={() => {
                    const supp = selectedPurchase.supplierName;
                    const pur = selectedPurchase;
                    setSelectedPurchase(null);
                    openReturnModal(supp, pur);
                  }}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs rounded-xl shadow transition-all flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path></svg>
                  ↩️ Return Unsold Stock of this Bill
                </button>
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl font-bold text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                  Print Bill
                </button>
                <button
                  onClick={() => handleDeletePurchase(selectedPurchase)}
                  className="px-4 py-2 bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white border border-rose-200 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer"
                  title="Delete this purchase bill"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Bill
                </button>
              </div>
              
              <button
                onClick={() => setSelectedPurchase(null)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs transition-colors shadow-sm"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Add Stockist Modal */}
      {showAddStockistModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-3 md:p-6 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-5 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path></svg>
                <h3 className="font-bold text-lg">Add New Stockist (Firestore)</h3>
              </div>
              <button onClick={() => setShowAddStockistModal(false)} className="text-white/80 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            <form onSubmit={handleSaveStockist} className="p-5 space-y-3.5">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Stockist / Party Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. BALAJI VETGEN PRIVATE LIMITED"
                  value={newStockist.name}
                  onChange={e => setNewStockist({...newStockist, name: e.target.value})}
                  className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-emerald-600 focus:bg-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Phone / Mobile</label>
                  <input
                    type="text"
                    placeholder="020-46919999"
                    value={newStockist.phone}
                    onChange={e => setNewStockist({...newStockist, phone: e.target.value})}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-emerald-600 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">GST Number</label>
                  <input
                    type="text"
                    placeholder="27AAKCB4237H1ZM"
                    value={newStockist.gst}
                    onChange={e => setNewStockist({...newStockist, gst: e.target.value})}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-emerald-600 focus:bg-white uppercase"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Drug License (DL No)</label>
                  <input
                    type="text"
                    placeholder="e.g. 20B/21B"
                    value={newStockist.dlNo}
                    onChange={e => setNewStockist({...newStockist, dlNo: e.target.value})}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-emerald-600 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Email</label>
                  <input
                    type="email"
                    placeholder="stockist@example.com"
                    value={newStockist.email}
                    onChange={e => setNewStockist({...newStockist, email: e.target.value})}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-emerald-600 focus:bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Address / Location</label>
                <textarea
                  rows="2"
                  placeholder="e.g. Kolhapur, Maharashtra"
                  value={newStockist.address}
                  onChange={e => setNewStockist({...newStockist, address: e.target.value})}
                  className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 outline-none focus:border-emerald-600 focus:bg-white"
                ></textarea>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowAddStockistModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl font-bold text-xs hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingStockist}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs shadow-md shadow-emerald-600/20"
                >
                  {isSavingStockist ? 'Saving to Firestore...' : 'Save Stockist'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Available Stock List Popup Modal */}
      {showStockModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-3 md:p-6 animate-in fade-in duration-200" onClick={() => setShowStockModal(false)}>
          <div className="bg-white w-full max-w-5xl rounded-3xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-700 via-teal-600 to-emerald-700 text-white p-4 md:p-5 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center text-xl shadow-inner">
                  📦
                </div>
                <div>
                  <h3 className="font-black text-lg md:text-xl">Available Stock in Medical Store</h3>
                  <p className="text-xs text-teal-100 font-semibold mt-0.5">उपलब्ध औषध साठा नोंद • Real-Time Inventory</p>
                </div>
              </div>
              <button 
                onClick={() => setShowStockModal(false)} 
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            {/* Modal KPI Summary Bar & Search */}
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3 shrink-0">
              <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
                <div className="bg-white border border-teal-200 px-3.5 py-1.5 rounded-xl shadow-xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">In Stock Medicines</span>
                  <span className="text-sm font-black text-teal-800">{availableStockMetrics.uniqueInStockCount} Medicines</span>
                </div>
                <div className="bg-white border border-emerald-200 px-3.5 py-1.5 rounded-xl shadow-xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Available Units</span>
                  <span className="text-sm font-black text-emerald-800">{availableStockMetrics.totalAvailableQty.toLocaleString()} Units</span>
                </div>
                <div className="bg-white border border-blue-200 px-3.5 py-1.5 rounded-xl shadow-xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Stock Valuation</span>
                  <span className="text-sm font-black text-blue-800">₹{availableStockMetrics.totalStockValue.toFixed(2)}</span>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative w-full md:w-72">
                <input
                  type="text"
                  value={stockModalSearch}
                  onChange={(e) => setStockModalSearch(e.target.value)}
                  placeholder="Search available stock medicine..."
                  className="w-full pl-9 pr-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-800 outline-none focus:border-teal-500 shadow-xs"
                />
                <svg className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                {stockModalSearch && (
                  <button onClick={() => setStockModalSearch('')} className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 font-bold text-xs">×</button>
                )}
              </div>
            </div>

            {/* Modal Table Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                <thead className="sticky top-0 z-10 bg-slate-100 shadow-[0_1px_0_rgba(0,0,0,0.05)]">
                  <tr className="text-slate-500 text-[10px] uppercase font-bold border-b border-slate-200">
                    <th className="py-2.5 px-3">Medicine Name</th>
                    <th className="py-2.5 px-3 text-center">Package / Packing</th>
                    <th className="py-2.5 px-3">Company / Mfg</th>
                    <th className="py-2.5 px-3 text-center">Batch & Exp</th>
                    <th className="py-2.5 px-3 text-center">Purchased Qty</th>
                    <th className="py-2.5 px-3 text-center">Sold Qty</th>
                    <th className="py-2.5 px-3 text-center text-teal-800 font-black">Available Stock</th>
                    <th className="py-2.5 px-3 text-right">Rate (₹)</th>
                    <th className="py-2.5 px-3 text-right">Stock Value (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {availableStockMetrics.itemsList
                    .filter(item => {
                      if (!stockModalSearch.trim()) return true;
                      const q = stockModalSearch.toLowerCase();
                      return (
                        item.name.toLowerCase().includes(q) ||
                        (item.packing && item.packing.toLowerCase().includes(q)) ||
                        (item.company && item.company.toLowerCase().includes(q)) ||
                        (item.batch && item.batch.toLowerCase().includes(q)) ||
                        (item.supplier && item.supplier.toLowerCase().includes(q))
                      );
                    })
                    .map((item, idx) => (
                      <tr key={idx} className="hover:bg-teal-50/40 transition-colors">
                        <td className="py-2.5 px-3 font-bold text-slate-800 uppercase">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span>{item.name}</span>
                            {item.packing && item.packing !== '-' && (
                              <span className="bg-teal-50 text-teal-700 border border-teal-200/90 px-1.5 py-0.5 rounded text-[10px] font-black">
                                {item.packing}
                              </span>
                            )}
                          </div>
                          {item.supplier && <div className="text-[9px] text-slate-400 font-semibold">Party: {item.supplier}</div>}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-lg font-black text-xs shadow-2xs inline-block">
                            {item.packing && item.packing !== '-' ? item.packing : '-'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-semibold text-slate-600 uppercase">
                          {item.company || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-center font-mono">
                          <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px] font-bold text-slate-700">{item.batch}</span>
                          {item.exp && <span className="text-[10px] text-slate-400 ml-1 font-semibold">({item.exp})</span>}
                        </td>
                        <td className="py-2.5 px-3 text-center font-bold text-slate-600">
                          {item.purchasedQty}
                        </td>
                        <td className="py-2.5 px-3 text-center font-bold text-slate-400">
                          {item.soldQty}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span className="bg-teal-100 text-teal-800 border border-teal-300 px-3 py-1 rounded-full font-black text-xs shadow-2xs inline-block">
                            {item.availableQty} Units
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-right font-bold text-slate-700">
                          ₹{item.rate.toFixed(2)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-black text-teal-700">
                          ₹{(item.availableQty * item.rate).toFixed(2)}
                        </td>
                      </tr>
                    ))}

                  {availableStockMetrics.itemsList.length === 0 && (
                    <tr>
                      <td colSpan="9" className="py-12 text-center text-slate-400 font-bold">
                        No available stock items found in medical store inventory.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center shrink-0">
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs shadow-sm flex items-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                Print Stock List
              </button>
              <button
                onClick={() => setShowStockModal(false)}
                className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-bold text-xs shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Stock to Party Modal */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[250] flex items-center justify-center p-3 md:p-6 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-700 via-teal-800 to-emerald-900 text-white p-5 flex justify-between items-start shrink-0">
              <div>
                <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-white text-[11px] font-bold uppercase tracking-wider">
                  Purchase Stock Return / Credit Note
                </span>
                <h3 className="text-xl md:text-2xl font-black mt-1 uppercase tracking-tight flex items-center gap-2">
                  <span>↩️ Return Stock to Party</span>
                </h3>
                <p className="text-xs text-teal-100 font-medium mt-0.5">
                  Select products to return to supplier party. Returned quantity clears available stock & hides items from Sales dropdown.
                </p>
              </div>

              <button 
                onClick={() => setShowReturnModal(false)} 
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-4 md:p-6 bg-slate-50 space-y-4">
              {/* Supplier Select */}
              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Select Supplier Party:</label>
                  <select
                    value={returnSupplierName}
                    onChange={(e) => {
                      setReturnSupplierName(e.target.value);
                      buildReturnItemList(e.target.value);
                    }}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 outline-none focus:border-teal-500"
                  >
                    {Array.from(new Set([
                      ...filteredStockists.map(s => s.name),
                      ...allPurchases.map(p => p.supplierName).filter(Boolean)
                    ])).map((name, i) => (
                      <option key={i} value={name}>{name}</option>
                    ))}
                  </select>
                </div>

                <div className="w-full sm:w-72">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Return Reason / Note:</label>
                  <input
                    type="text"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    placeholder="e.g. Unsold Stock Return / Expiry"
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-teal-500"
                  />
                </div>
              </div>

              {/* Items Table */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Expiry Filter Tabs & Action Bar */}
                <div className="p-3 bg-slate-100 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setReturnFilterTab('EXPIRED')}
                      className={`px-3 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer ${
                        returnFilterTab === 'EXPIRED'
                          ? 'bg-rose-600 text-white shadow-sm shadow-rose-600/30'
                          : 'bg-white text-slate-700 hover:bg-rose-50 border border-slate-200'
                      }`}
                    >
                      <span>⚠️ Expired Only</span>
                      <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${returnFilterTab === 'EXPIRED' ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-700 font-black'}`}>
                        {returnItems.filter(r => r.expiryInfo?.isExpired && r.availableStock > 0).length}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setReturnFilterTab('NEAR_EXPIRY')}
                      className={`px-3 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer ${
                        returnFilterTab === 'NEAR_EXPIRY'
                          ? 'bg-amber-600 text-white shadow-sm shadow-amber-600/30'
                          : 'bg-white text-slate-700 hover:bg-amber-50 border border-slate-200'
                      }`}
                    >
                      <span>⏳ Near Expiry</span>
                      <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${returnFilterTab === 'NEAR_EXPIRY' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-800 font-bold'}`}>
                        {returnItems.filter(r => r.expiryInfo?.isNearExpiry && r.availableStock > 0).length}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setReturnFilterTab('ALL')}
                      className={`px-3 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer ${
                        returnFilterTab === 'ALL'
                          ? 'bg-teal-700 text-white shadow-sm shadow-teal-700/30'
                          : 'bg-white text-slate-700 hover:bg-teal-50 border border-slate-200'
                      }`}
                    >
                      <span>📦 All Products</span>
                      <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${returnFilterTab === 'ALL' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'}`}>
                        {returnItems.length}
                      </span>
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setReturnItems(returnItems.map(r => {
                          const isExp = !!(r.expiryInfo?.isExpired && r.availableStock > 0);
                          return {
                            ...r,
                            selected: isExp,
                            returnQty: isExp ? r.availableStock : 0,
                            amount: isExp ? r.availableStock * r.rate : 0
                          };
                        }));
                        setReturnFilterTab('EXPIRED');
                        setReturnReason('Expired Medicine Stock Return to Party / Expiry Return');
                      }}
                      className="px-2.5 py-1 bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white rounded-lg font-bold text-[11px] border border-rose-200 transition-all flex items-center gap-1 cursor-pointer"
                      title="Select all expired items only"
                    >
                      <span>⚡ Select Expired Only</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const allSelected = returnItems.every(r => r.selected);
                        setReturnItems(returnItems.map(r => ({ ...r, selected: !allSelected })));
                      }}
                      className="text-teal-700 hover:underline text-[11px] font-bold cursor-pointer"
                    >
                      Toggle All
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold border-b border-slate-200">
                        <th className="p-3 text-center">Select</th>
                        <th className="p-3">Product Name & Mfg</th>
                        <th className="p-3 text-center">Package</th>
                        <th className="p-3">Batch / Expiry Status</th>
                        <th className="p-3 text-center">Purchased</th>
                        <th className="p-3 text-center">Sold</th>
                        <th className="p-3 text-center">Current Stock</th>
                        <th className="p-3 text-center">Return Qty</th>
                        <th className="p-3 text-right">Rate (₹)</th>
                        <th className="p-3 text-right">Return Amt (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {returnItems.filter(item => {
                        if (returnFilterTab === 'EXPIRED') return item.expiryInfo?.isExpired;
                        if (returnFilterTab === 'NEAR_EXPIRY') return item.expiryInfo?.isNearExpiry;
                        return true;
                      }).length === 0 ? (
                        <tr>
                          <td colSpan="10" className="p-8 text-center text-slate-400 font-bold">
                            {returnFilterTab === 'EXPIRED' 
                              ? '✅ Great news! No expired medicines found for this party.'
                              : returnFilterTab === 'NEAR_EXPIRY'
                              ? 'No near-expiry medicines found for this party.'
                              : 'No purchased items found for this party.'}
                          </td>
                        </tr>
                      ) : (
                        returnItems
                          .map((item, originalIdx) => ({ item, originalIdx }))
                          .filter(({ item }) => {
                            if (returnFilterTab === 'EXPIRED') return item.expiryInfo?.isExpired;
                            if (returnFilterTab === 'NEAR_EXPIRY') return item.expiryInfo?.isNearExpiry;
                            return true;
                          })
                          .map(({ item, originalIdx }) => (
                            <tr 
                              key={originalIdx} 
                              className={`hover:bg-slate-50 transition-colors ${
                                item.expiryInfo?.isExpired 
                                  ? 'bg-rose-50/40 border-l-4 border-l-rose-500' 
                                  : item.selected ? 'bg-teal-50/40' : ''
                              }`}
                            >
                              <td className="p-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={item.selected}
                                  onChange={(e) => {
                                    const val = e.target.checked;
                                    setReturnItems(returnItems.map((r, i) => i === originalIdx ? { ...r, selected: val } : r));
                                  }}
                                  className="w-4 h-4 text-teal-600 rounded cursor-pointer"
                                />
                              </td>
                              <td className="p-3 font-bold text-slate-800 uppercase">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span>{item.productName}</span>
                                  {item.packing && item.packing !== '-' && (
                                    <span className="bg-teal-50 text-teal-700 border border-teal-200/90 px-1.5 py-0.5 rounded text-[10px] font-black">
                                      {item.packing}
                                    </span>
                                  )}
                                </div>
                                {item.company && <span className="text-[10px] text-slate-400 font-normal block">{item.company}</span>}
                              </td>
                              <td className="p-3 text-center">
                                <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-lg font-black text-xs shadow-2xs inline-block">
                                  {item.packing && item.packing !== '-' ? item.packing : '-'}
                                </span>
                              </td>
                              <td className="p-3 font-semibold text-slate-600">
                                <div>{item.batch}</div>
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                  <span className="text-[10px] text-slate-500 font-medium">Exp: {item.exp}</span>
                                  {item.expiryInfo?.isExpired && (
                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-black bg-rose-100 text-rose-700 border border-rose-300">
                                      ⚠️ EXPIRED
                                    </span>
                                  )}
                                  {item.expiryInfo?.isNearExpiry && (
                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                                      ⏳ NEAR EXPIRY
                                    </span>
                                  )}
                                  {!item.expiryInfo?.isExpired && !item.expiryInfo?.isNearExpiry && (
                                    <span className="px-1.5 py-0.2 rounded text-[10px] bg-slate-100 text-slate-500">
                                      Valid
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-3 text-center font-bold text-slate-600">{item.purchasedQty}</td>
                              <td className="p-3 text-center font-bold text-amber-600">{item.soldQty}</td>
                              <td className="p-3 text-center font-black">
                                <span className={`px-2 py-0.5 rounded text-[11px] ${item.availableStock > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
                                  {item.availableStock}
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                <input
                                  type="number"
                                  min="0"
                                  max={item.availableStock}
                                  value={item.returnQty}
                                  onChange={(e) => {
                                    const qty = parseFloat(e.target.value) || 0;
                                    setReturnItems(returnItems.map((r, i) => i === originalIdx ? {
                                      ...r,
                                      returnQty: qty,
                                      selected: qty > 0,
                                      amount: qty * r.rate
                                    } : r));
                                  }}
                                  className="w-16 text-center font-bold bg-white border border-slate-300 rounded px-1.5 py-1 text-xs text-slate-800 outline-none focus:border-teal-500"
                                />
                              </td>
                              <td className="p-3 text-right font-semibold text-slate-600">₹{item.rate.toFixed(2)}</td>
                              <td className="p-3 text-right font-black text-teal-700">
                                ₹{((parseFloat(item.returnQty) || 0) * item.rate).toFixed(2)}
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-3 shrink-0">
              <div className="text-xs font-bold text-slate-600">
                Total Credit Value: <span className="text-lg font-black text-teal-700 ml-1">
                  ₹{returnItems.filter(r => r.selected).reduce((sum, r) => sum + (parseFloat(r.returnQty) || 0) * r.rate, 0).toFixed(2)}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-xl font-bold text-xs"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSaveStockReturn}
                  disabled={isSubmittingReturn}
                  className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold text-xs shadow-md shadow-teal-600/20 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmittingReturn ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                      <span>Saving Return...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Return to Party (Create Credit Note)</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Purchase Confirmation Modal */}
      {deleteConfirmPurchase && (
        <div 
          onClick={() => setDeleteConfirmPurchase(null)}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-rose-200 animate-in zoom-in-95 duration-200"
          >
            <div className="p-6 bg-gradient-to-r from-rose-600 to-red-700 text-white text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 text-2xl backdrop-blur-xs">
                🗑️
              </div>
              <h3 className="text-xl font-black">Delete Purchase Bill?</h3>
              <p className="text-xs text-rose-100 font-medium mt-1">This action cannot be undone</p>
            </div>

            <div className="p-6 space-y-3.5 text-xs bg-white">
              <div className="p-3.5 bg-rose-50 border border-rose-200/80 rounded-2xl space-y-2 font-bold text-slate-700">
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Invoice No:</span>
                  <span className="text-slate-900 font-black">{deleteConfirmPurchase.invoiceNo || 'N/A'}</span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Stockist / Supplier:</span>
                  <span className="text-slate-900 font-black uppercase">{deleteConfirmPurchase.supplierName || 'Unknown Party'}</span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Bill Date:</span>
                  <span className="text-slate-900">{deleteConfirmPurchase.date || '-'}</span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Items:</span>
                  <span className="text-slate-900">{(deleteConfirmPurchase.items && deleteConfirmPurchase.items.length) || 0} Products</span>
                </div>
                <div className="flex justify-between pt-1 text-sm font-black text-rose-700">
                  <span>Net Amount:</span>
                  <span>₹{parseFloat(deleteConfirmPurchase.netAmount || deleteConfirmPurchase.totalAmount || deleteConfirmPurchase.grossAmount || 0).toFixed(2)}</span>
                </div>
              </div>

              <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                Deleting this purchase invoice will remove it from <strong>Stockist Purchases</strong>, <strong>Monthly Reports</strong>, and linked <strong>Near Expiry / Stock Tracking</strong> records.
              </p>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2.5">
              <button
                type="button"
                disabled={isDeletingPurchase}
                onClick={() => setDeleteConfirmPurchase(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-xl text-xs transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeletingPurchase}
                onClick={confirmDeletePurchase}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white font-black rounded-xl text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {isDeletingPurchase ? 'Deleting...' : 'Yes, Delete Bill'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Stockist Confirmation Modal */}
      {deleteConfirmStockist && (
        <div 
          onClick={() => setDeleteConfirmStockist(null)}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-rose-200 animate-in zoom-in-95 duration-200"
          >
            <div className="p-6 bg-gradient-to-r from-rose-600 to-red-700 text-white text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 text-2xl backdrop-blur-xs">
                🏢
              </div>
              <h3 className="text-xl font-black">Delete Stockist Party?</h3>
              <p className="text-xs text-rose-100 font-medium mt-1">This action cannot be undone</p>
            </div>

            <div className="p-6 space-y-3.5 text-xs bg-white">
              <div className="p-3.5 bg-rose-50 border border-rose-200/80 rounded-2xl space-y-2 font-bold text-slate-700">
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Stockist Name:</span>
                  <span className="text-slate-900 font-black uppercase">{deleteConfirmStockist.name}</span>
                </div>
                {deleteConfirmStockist.phone && (
                  <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                    <span className="text-slate-400">Contact / Phone:</span>
                    <span className="text-slate-900">{deleteConfirmStockist.phone}</span>
                  </div>
                )}
                {deleteConfirmStockist.gst && (
                  <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                    <span className="text-slate-400">GST No:</span>
                    <span className="text-slate-900">{deleteConfirmStockist.gst}</span>
                  </div>
                )}
                <div className="flex justify-between pt-1 text-sm font-black text-rose-700">
                  <span>Total Purchases:</span>
                  <span>₹{parseFloat(deleteConfirmStockist.totalPurchases || 0).toFixed(2)}</span>
                </div>
              </div>

              <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                Deleting this stockist will remove their details from the <strong>Stockist Directory Master</strong>.
              </p>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2.5">
              <button
                type="button"
                disabled={isDeletingStockist}
                onClick={() => setDeleteConfirmStockist(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-xl text-xs transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeletingStockist}
                onClick={confirmDeleteStockist}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white font-black rounded-xl text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {isDeletingStockist ? 'Deleting...' : 'Yes, Delete Stockist'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Credit Note Confirmation Modal */}
      {deleteConfirmCreditNote && (
        <div 
          onClick={() => setDeleteConfirmCreditNote(null)}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-rose-200 animate-in zoom-in-95 duration-200"
          >
            <div className="p-6 bg-gradient-to-r from-rose-600 to-red-700 text-white text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 text-2xl backdrop-blur-xs">
                💳
              </div>
              <h3 className="text-xl font-black">Delete Credit Note / Return?</h3>
              <p className="text-xs text-rose-100 font-medium mt-1">This action cannot be undone</p>
            </div>

            <div className="p-6 space-y-3.5 text-xs bg-white">
              <div className="p-3.5 bg-rose-50 border border-rose-200/80 rounded-2xl space-y-2 font-bold text-slate-700">
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Credit Note No:</span>
                  <span className="text-slate-900 font-black">{deleteConfirmCreditNote.creditNoteNo || 'CN-MEMO'}</span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Stockist / Party:</span>
                  <span className="text-slate-900 font-black uppercase">{deleteConfirmCreditNote.supplierName || 'Unknown'}</span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Date:</span>
                  <span className="text-slate-900">{deleteConfirmCreditNote.date || '-'}</span>
                </div>
                <div className="flex justify-between pt-1 text-sm font-black text-rose-700">
                  <span>Credited Value:</span>
                  <span>₹{parseFloat(deleteConfirmCreditNote.totalAmount || 0).toFixed(2)}</span>
                </div>
              </div>

              <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                Deleting this credit note will permanently remove the returned stock entry from the ledger.
              </p>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2.5">
              <button
                type="button"
                disabled={isDeletingCreditNote}
                onClick={() => setDeleteConfirmCreditNote(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-xl text-xs transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeletingCreditNote}
                onClick={confirmDeleteCreditNote}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white font-black rounded-xl text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {isDeletingCreditNote ? 'Deleting...' : 'Yes, Delete Credit Note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockistPurchases;
